import type { Ruolo, UtenteAdmin } from '@aulaviva/shared';
import { Alert, Button, DataTable, Dialog, EmptyState, NativeSelect, useToast, type ColumnDef } from '@aulaviva/ui';
import { Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useCambiaRuolo, useUtentiAdmin } from '@/api/admin';
import { formattaDataOra } from '@/lib/date';
import { admin, comuni } from '@/testi';
import { CampoRicerca, IntestazioneAdmin } from './comuni';

export function Utenti() {
  const t = admin.utenti;
  const toast = useToast();
  const [q, setQ] = useState('');
  const [ruolo, setRuolo] = useState('');
  const [pagina, setPagina] = useState(1);
  const utenti = useUtentiAdmin({ q: q || undefined, ruolo: ruolo || undefined, pagina, perPagina: 50 });
  const cambia = useCambiaRuolo();
  const [daCambiare, setDaCambiare] = useState<UtenteAdmin | null>(null);
  const [nuovoRuolo, setNuovoRuolo] = useState<Ruolo>('partecipante');

  const colonne = useMemo<ColumnDef<UtenteAdmin, unknown>[]>(
    () => [
      { header: t.colonne.nome, cell: ({ row }) => <Link to={`/admin/utenti/${row.original.id}`}>{`${row.original.cognome} ${row.original.nome}`}</Link> },
      { header: t.colonne.email, accessorKey: 'email' },
      { header: t.colonne.ruolo, cell: ({ row }) => admin.ruoli[row.original.ruolo] },
      { header: t.colonne.stato, cell: ({ row }) => admin.statiUtente[row.original.stato] },
      { header: t.colonne.ultimoAccesso, cell: ({ row }) => (row.original.ultimoAccessoAt ? formattaDataOra(row.original.ultimoAccessoAt) : t.mai) },
      { header: t.colonne.creato, cell: ({ row }) => formattaDataOra(row.original.createdAt) },
      {
        id: 'azioni',
        header: '',
        cell: ({ row }) => (
          <Button variant="discreet" onClick={() => { setDaCambiare(row.original); setNuovoRuolo(row.original.ruolo); }}>
            {t.cambiaRuolo}
          </Button>
        ),
      },
    ],
    [t],
  );

  async function conferma() {
    if (!daCambiare) return;
    try {
      await cambia.mutateAsync({ id: daCambiare.id, ruolo: nuovoRuolo });
      setDaCambiare(null);
      toast.mostra({ variant: 'success', title: t.cambiato });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  const vuoto = !q && !ruolo;
  return (
    <>
      <IntestazioneAdmin titolo={t.titolo} sottotitolo={t.sottotitolo} />
      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4 items-end">
        <CampoRicerca valore={q} onCambia={(v) => { setQ(v); setPagina(1); }} hint={admin.comuni.cercaAiuto} />
        <NativeSelect
          label={t.filtroRuolo}
          placeholder={admin.comuni.tutti}
          value={ruolo}
          onChange={(e) => { setRuolo(e.target.value); setPagina(1); }}
          options={Object.entries(admin.ruoli).map(([value, label]) => ({ value, label }))}
        />
      </div>
      {utenti.isError && <Alert variant="error" title={utenti.error.message} />}
      <DataTable
        caption={t.titolo}
        columns={colonne}
        data={utenti.data?.elementi ?? []}
        getRowId={(u) => u.id}
        loading={utenti.isPending}
        testi={admin.comuni}
        pagination={utenti.data ? { pagina: utenti.data.pagina, perPagina: utenti.data.perPagina, totale: utenti.data.totale, onPagina: setPagina } : undefined}
        empty={
          <EmptyState icon={Users} title={vuoto ? t.nessunoTitolo : t.nessunRisultato}>
            {vuoto ? t.nessunoTesto : null}
          </EmptyState>
        }
      />
      <Dialog
        open={daCambiare !== null}
        onOpenChange={(o) => !o && setDaCambiare(null)}
        title={daCambiare ? t.cambiaRuoloTitolo(`${daCambiare.nome} ${daCambiare.cognome}`) : ''}
        actions={
          <>
            <Button onClick={() => setDaCambiare(null)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={conferma} loading={cambia.isPending} loadingText={admin.comuni.inCorso}>
              {t.conferma}
            </Button>
          </>
        }
      >
        <NativeSelect label={t.nuovoRuolo} value={nuovoRuolo} onChange={(e) => setNuovoRuolo(e.target.value as Ruolo)} options={Object.entries(admin.ruoli).map(([value, label]) => ({ value, label }))} />
        {nuovoRuolo === 'relatore' && <Alert variant="info" title={t.avvisoRelatore} />}
        {nuovoRuolo === 'admin' && <Alert variant="warning" title={t.avvisoAdmin} />}
      </Dialog>
    </>
  );
}
